import React, { useState, useEffect, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { hrAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate = d => formatDateDMY(d, '—');

const HR = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSecondary = { padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [showAttForm, setShowAttForm] = useState(false);

  const [empForm, setEmpForm] = useState({ name: '', phone: '', designation: '', department: '', basic_salary: 0, hra: 0, da: 0, other_allowances: 0, joining_date: new Date().toISOString().slice(0, 10), bank_account: '', bank_ifsc: '' });
  const [attForm, setAttForm] = useState({ employee_id: '', attendance_date: new Date().toISOString().slice(0, 10), status: 'present', check_in: '', check_out: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'employees') { const r = await hrAPI.listEmployees(); setEmployees(Array.isArray(r.data) ? r.data : []); }
      else if (tab === 'payroll') { const r = await hrAPI.listSalaries(); setSalaries(Array.isArray(r.data) ? r.data : []); }
      else if (tab === 'attendance') { const r = await hrAPI.listAttendance(); setAttendance(Array.isArray(r.data?.records) ? r.data.records : []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load employees for dropdowns when needed
  useEffect(() => {
    if (tab !== 'employees' && employees.length === 0) {
      hrAPI.listEmployees().then(r => setEmployees(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [tab, employees.length]);

  const createEmployee = async (e) => {
    e.preventDefault();
    try { await hrAPI.createEmployee(empForm); setShowEmpForm(false); setEmpForm({ name: '', phone: '', designation: '', department: '', basic_salary: 0, hra: 0, da: 0, other_allowances: 0, joining_date: new Date().toISOString().slice(0, 10), bank_account: '', bank_ifsc: '' }); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const generateSalary = async (empId) => {
    const now = new Date();
    try {
      await hrAPI.createSalary({ employee_id: empId, month: now.getMonth() + 1, year: now.getFullYear() });
      if (tab === 'payroll') loadData();
      else { setTab('payroll'); }
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const bulkGenerate = async () => {
    const now = new Date();
    try { await hrAPI.bulkGenerateSalary({ month: now.getMonth() + 1, year: now.getFullYear() }); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const approveSalary = async (id) => { try { await hrAPI.approveSalary(id); loadData(); } catch (e) { alert('Failed'); } };
  const paySalary = async (id) => { try { await hrAPI.paySalary(id); loadData(); } catch (e) { alert('Failed'); } };

  const markAttendance = async (e) => {
    e.preventDefault();
    try { await hrAPI.markAttendance(attForm); setShowAttForm(false); setAttForm({ employee_id: '', attendance_date: new Date().toISOString().slice(0, 10), status: 'present', check_in: '', check_out: '' }); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { key: 'employees', label: '👤 Employees' },
    { key: 'payroll', label: '💰 Payroll' },
    { key: 'attendance', label: '📋 Attendance' },
  ];

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, marginBottom: 20 }}>👔 HR & Payroll</h1>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${tab === t.key ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: tab === t.key ? acc : 'transparent', color: tab === t.key ? '#fff' : pg.color, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div>}

        {/* Employees */}
        {tab === 'employees' && !loading && (
          <div>
            <button onClick={() => setShowEmpForm(!showEmpForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showEmpForm ? 'Cancel' : '+ Add Employee'}</button>

            {showEmpForm && (
              <form onSubmit={createEmployee} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                  <div><label style={lbl}>Name *</label><input value={empForm.name} onChange={e => setEmpForm(f => ({ ...f, name: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Phone</label><input value={empForm.phone} onChange={e => setEmpForm(f => ({ ...f, phone: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Role</label><input value={empForm.designation} onChange={e => setEmpForm(f => ({ ...f, designation: e.target.value }))} style={inp} placeholder="Manager, Sales, etc." /></div>
                  <div><label style={lbl}>Department</label><input value={empForm.department} onChange={e => setEmpForm(f => ({ ...f, department: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Join Date</label><StrictDateInput value={empForm.joining_date} onChange={e => setEmpForm(f => ({ ...f, joining_date: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Basic Salary (₹)</label><input type="number" value={empForm.basic_salary} onChange={e => setEmpForm(f => ({ ...f, basic_salary: Number(e.target.value) }))} style={inp} /></div>
                  <div><label style={lbl}>HRA (₹)</label><input type="number" value={empForm.hra} onChange={e => setEmpForm(f => ({ ...f, hra: Number(e.target.value) }))} style={inp} /></div>
                  <div><label style={lbl}>DA (₹)</label><input type="number" value={empForm.da} onChange={e => setEmpForm(f => ({ ...f, da: Number(e.target.value) }))} style={inp} /></div>
                  <div><label style={lbl}>Other Allowances (₹)</label><input type="number" value={empForm.other_allowances} onChange={e => setEmpForm(f => ({ ...f, other_allowances: Number(e.target.value) }))} style={inp} /></div>
                  <div><label style={lbl}>Bank A/c No</label><input value={empForm.bank_account} onChange={e => setEmpForm(f => ({ ...f, bank_account: e.target.value }))} style={inp} /></div>
                </div>
                <button type="submit" style={btnPrimary}>Add Employee</button>
              </form>
            )}

            {employees.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No employees added yet</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Name', 'Phone', 'Role', 'Dept', 'Basic Salary', 'Joined', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{emp.name}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{emp.phone || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{emp.designation || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{emp.department || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>₹{fmtNum(emp.basic_salary)}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(emp.joining_date)}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: emp.status === 'active' ? '#DCFCE7' : '#FEE2E2', color: emp.status === 'active' ? '#166534' : '#991B1B' }}>
                            {emp.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <button onClick={() => generateSalary(emp.id)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11 }}>Gen Salary</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payroll */}
        {tab === 'payroll' && !loading && (
          <div>
            <button onClick={bulkGenerate} style={{ ...btnPrimary, marginBottom: 16 }}>Bulk Generate This Month</button>

            {salaries.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No salary records yet</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Month', 'Employee', 'Basic', 'HRA', 'Gross', 'PF', 'ESI', 'Net Pay', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', ...muted, fontWeight: 700, fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {salaries.map(s => (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '6px 8px', ...pg }}>{s.month}/{s.year}</td>
                        <td style={{ padding: '6px 8px', ...pg, fontWeight: 600 }}>{s.employee_id?.substring(0, 8) || '—'}</td>
                        <td style={{ padding: '6px 8px', ...pg }}>₹{fmtNum(s.basic)}</td>
                        <td style={{ padding: '6px 8px', ...pg }}>₹{fmtNum(s.hra)}</td>
                        <td style={{ padding: '6px 8px', ...pg }}>₹{fmtNum((Number(s.basic || 0) + Number(s.hra || 0) + Number(s.da || 0) + Number(s.allowances || 0) + Number(s.bonus || 0)))}</td>
                        <td style={{ padding: '6px 8px', ...pg }}>₹{fmtNum(s.pf_amount)}</td>
                        <td style={{ padding: '6px 8px', ...pg }}>₹{fmtNum(s.esi_amount)}</td>
                        <td style={{ padding: '6px 8px', ...pg, fontWeight: 700 }}>₹{fmtNum(s.net_salary)}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: s.status === 'paid' ? '#DCFCE7' : s.status === 'approved' ? '#DBEAFE' : '#FEF3C7', color: s.status === 'paid' ? '#166534' : s.status === 'approved' ? '#1E40AF' : '#92400E' }}>
                            {s.status?.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '6px 8px', display: 'flex', gap: 4 }}>
                          {s.status === 'draft' && <button onClick={() => approveSalary(s.id)} style={{ ...btnSecondary, padding: '3px 8px', fontSize: 10 }}>Approve</button>}
                          {s.status === 'approved' && <button onClick={() => paySalary(s.id)} style={{ ...btnPrimary, padding: '3px 8px', fontSize: 10 }}>Pay</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Attendance */}
        {tab === 'attendance' && !loading && (
          <div>
            <button onClick={() => setShowAttForm(!showAttForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showAttForm ? 'Cancel' : '+ Mark Attendance'}</button>

            {showAttForm && (
              <form onSubmit={markAttendance} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                  <div><label style={lbl}>Employee</label>
                    <select value={attForm.employee_id} onChange={e => setAttForm(f => ({ ...f, employee_id: e.target.value }))} style={inp} required>
                      <option value="">Select</option>
                      {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>Date</label><StrictDateInput value={attForm.attendance_date} onChange={e => setAttForm(f => ({ ...f, attendance_date: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Status</label>
                    <select value={attForm.status} onChange={e => setAttForm(f => ({ ...f, status: e.target.value }))} style={inp}>
                      {['present', 'absent', 'half_day', 'leave', 'holiday'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>Check-in</label><input type="time" value={attForm.check_in} onChange={e => setAttForm(f => ({ ...f, check_in: e.target.value }))} style={inp} /></div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}><button type="submit" style={btnPrimary}>Mark</button></div>
                </div>
              </form>
            )}

            {attendance.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No attendance records</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Date', 'Employee', 'Status', 'Check-in', 'Check-out'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.map(a => (
                      <tr key={a.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(a.attendance_date)}</td>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{a.employee_id?.substring(0, 8)}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: a.status === 'present' ? '#DCFCE7' : a.status === 'absent' ? '#FEE2E2' : '#FEF3C7', color: a.status === 'present' ? '#166534' : a.status === 'absent' ? '#991B1B' : '#92400E' }}>
                            {a.status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', ...pg }}>{a.check_in || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{a.check_out || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HR;
